import { T } from '@tolgee/react';
import { OptimizedImage } from '../../../../components/ui/OptimizedImage';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const BrandsFeature = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleViewProducts = (brand: string) => {
    navigate(`/products?page=1&brand=${encodeURIComponent(brand)}`);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -320, // Ancho de una tarjeta + espacio
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 320, // Ancho de una tarjeta + espacio
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="overflow-x-hidden">
      <hr className="border-t-2 border-gray-200 my-10" />
      <div className="h-32 flex sm:flex-row flex-col justify-center mx-2 px-10 py-24 sm:py-10">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mr-auto">
          <T keyName="brands.title">Nuestras Marcas</T>
        </h2>
        <p className="mt-4 text-s text-gray-8s00 w-full sm:w-1/3 bold">
          <T keyName="brands.subtitle">Cada mascota tiene necesidades únicas, por eso hemos desarrollado una amplia variedad de líneas de productos para ofrecer soluciones que se adapten a todo tipo de animales y preferencias.</T>
        </p>
      </div>
      {/* Contenedor con scroll snap horizontal */}
      <h3 className="text-2xl font-bold text-gray-900 px-10 mx-2">
        <T keyName="brands.birds_rodents.title">Aves y Roedores</T>
      </h3>

      {/* Contenedor relativo para los botones */}
      <div className="relative px-10">
        {/* Botón izquierdo */}
        <button
          onClick={scrollLeft}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors hover:shadow-xl"
          aria-label="Scroll izquierda"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>

        {/* Botón derecho */}
        <button
          onClick={scrollRight}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors hover:shadow-xl"
          aria-label="Scroll derecha"
        >
          <ChevronRight className="w-6 h-6 text-gray-600" />
        </button>

        <div
          ref={scrollContainerRef}
          className="overflow-x-auto snap-x snap-mandatory py-10 px-16"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: 'none'
          } as React.CSSProperties & { WebkitScrollbar?: string }}
        >
          <div className="flex space-x-6 px-6 pb-8 w-max">

            {/* Tarjeta PREMIfood */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-premifood.webp"
                  alt="Jarad PREMIfood"
                  width={200}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col ">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.premifood.title">Alimento premium para aves y roededores</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.premifood.description">Productos 100% naturales, ideales tanto para competición como para quienes desean ofrecer a sus aves y roedores una alimentación premium que garantice su salud y rendimiento.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('PREMIfood')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta La Mejor Selección */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-mejor-seleccion.webp"
                  alt="La Mejor Selección"
                  width={200}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.mejor_seleccion.title">Alimentación especializada para criadores y competidores</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.mejor_seleccion.description">Esta línea está diseñada para expertos dedicados a la cría y competición intensiva de roedores, ofreciendo fórmulas especializadas que aseguran un óptimo desarrollo y condición física.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Jarad La Mejor Selección')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta Active Menú */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-active.webp"
                  alt="Jarad Active Menú"
                  width={239}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.active.title">Alimentación completa y equilibrada para aves y roedores</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.active.description">Alimentos formulados específicamente para cubrir todas las necesidades nutricionales diarias de aves y roedores, garantizando su salud y vitalidad.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Active Menú')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta Diet Complet */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-diet.webp"
                  alt="Diet Complet"
                  width={200}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.diet.title">Alimento extrusionado innovador para aves</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.diet.description">Producto innovador elaborado con materias primas seleccionadas, en formato extrusionado y porciones pequeñas, para una alimentación práctica y nutritiva de aves.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Diet Complet')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta Zoo Campero */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-zoo-campero.webp"
                  alt="Zoo Campero"
                  width={200}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.zoo_campero.title">Piensos granulados completos para animales de granja</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.zoo_campero.description">Alimentos completos que proporcionan una dieta rica y equilibrada para animales de granja, contribuyendo a su bienestar y productividad.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Zoo Campero')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta Jarad Snacks */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-snack.webp"
                  alt="Jarad Snacks"
                  width={223}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.snacks.title">Snacks naturales llenos de sabor y nutrientes para aves y roedores</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.snacks.description">Snacks con una excelente fuente de energía y nutrientes adicionales, que les ayudan a mantener un alto nivel de actividad gracias al aporte de ingredientes naturales como la miel, el huevo o la fruta.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Jarad Snacks')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Sección Perros y Gatos */}
      <h3 className="text-2xl font-bold text-gray-900 px-10 mx-2 mt-16">
        <T keyName="brands.dogs_cats.title">Perros y Gatos</T>
      </h3>
      <div className="w-full flex flex-wrap">
        <div
          className="overflow-x-auto snap-x snap-mandatory py-10 px-10 w-full sm:w-[850px]"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: 'none'
          } as React.CSSProperties & { WebkitScrollbar?: string }}
        >
          <div className="flex space-x-6 px-6 pb-8 w-max">

            {/* Tarjeta VITAL */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-vital.webp"
                  alt="Jarad VITAL"
                  width={200}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.vital.title">Alimentación natural para perros y gatos</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.vital.description">La línea Jarad VITAL es para aquellos que buscan una nutrición fiable y equilibrada para perros y gatos, con productos de calidad que cubren todas las necesidades básicas de las mascotas.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Jarad Vital')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

            {/* Tarjeta Plus */}
            <div className="snap-center flex-shrink-0 w-80 bg-white rounded-2xl shadow-lg overflow-hidden border">
              <div className="relative h-40 flex items-center justify-center">
                <OptimizedImage
                  src="/images/brands/jarad-plus.webp"
                  alt="Jarad Plus"
                  width={128}
                  height={160}
                  type="product"
                  className="h-40 object-contain"
                />
              </div>
              <div className="p-6 h-[400px] flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <T keyName="brands.plus.title">Alimento premium para perros y gatos</T>
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  <T keyName="brands.plus.description">Es un alimento premium desarrollado con los mejores ingredientes naturales. Diseñado para satisfacer de manera integral las necesidades nutricionales de perros y gatos.</T>
                </p>
                <button
                  onClick={() => handleViewProducts('Jarad Plus')}
                  className="bg-black text-white py-2 px-4 rounded-full hover:bg-gray-800 transition-colors text-sm w-fit self-start mt-auto"
                >
                  <T keyName="brands.view_products">Ver productos</T>
                </button>
              </div>
            </div>

          </div>
        </div>
        <div className="w-full min-w-[300px] h-[500px] lg:w-[calc(100%-850px)] lg:h-auto">
          <div className="relative w-full h-full flex items-center justify-end">
            <svg
              className="absolute inset-0 w-full h-full opacity-100 transform translate-x-1/4 min-w-[300px] min-h-[300px]"
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="#f6cf02"
              />
            </svg>
            <OptimizedImage
              src="/images/perro-gato-jarad.webp"
              alt="Perro y Gato Jarad"
              width={400}
              height={350}
              type="product"
              className="absolute z-10 max-h-[350px] max-w-[90%] object-contain right-0"
              style={{ top: '50%', right: '0', transform: 'translateY(-55%)' }}
            />
          </div>
        </div>
      </div>

    </section>
  );
};

export default BrandsFeature; 